import { useMemo } from "react";
import { useLocation } from "react-router-dom";

/**
 * Parse query parameters from a full path string like `/path?foo=bar&baz=qux`.
 * Returns an empty object if there is no query string.
 */
function parseQueryParams(path: string): Record<string, string> {
  const index = path.indexOf("?");
  if (index === -1) return {};
  const params: Record<string, string> = {};
  for (const pair of path.slice(index + 1).split("&")) {
    const [key, value] = pair.split("=");
    if (key) {
      params[decodeURIComponent(key)] = decodeURIComponent(value ?? "");
    }
  }
  return params;
}

/**
 * Read the current route's query parameters.
 *
 * ```ts
 * const { email, token } = useRouteQueryParams();
 * ```
 *
 * From the router's own location, never `window.location.search`, which is always empty
 * under native navigation because the webview loads its HTML via `loadHTMLString`.
 * react-router is unaffected: the native integration pushes each view under its full path
 * and renders it inside its own `<Router location={parsePath(path)} />`.
 *
 * Not from a module-level store, which cannot serve a stack of views: a parent stays
 * mounted while a sub screen sits on top of it, and the sub screen's own query-less push
 * would clear the parent's params underneath it.
 */
export function useRouteQueryParams(): Record<string, string> {
  const { search } = useLocation();
  return useMemo(() => parseQueryParams(search), [search]);
}
